/**
 * ability-service.ts
 *
 * Client service for Asset Abilities (Phase 1): ai-search and content-resolution.
 * Provides typed helpers to fetch/update abilities and trigger resync.
 */

// --- TYPES ---
export type AiSearchSyncedFile = {
  sourceKey: string;
  destinationKey: string;
  bytes: number;
  lastSyncedAt: string;
  indexingStatus?: 'pending' | 'indexed' | 'error';
  indexingDetail?: string;
};

export type AiSearchAbilityStatus = {
  lastSyncedAt?: string;
  objectCount?: number;
  totalBytes?: number;
  warnings?: string[];
  files?: AiSearchSyncedFile[];
  // Note: syncJobId, syncJobStatus, syncError NOT stored in status
  // Job status is queried from AutoRAG API on demand
};

export type AiSearchAbilityState = {
  enabled: boolean;
  actionId?: string;
  searchActionId?: string;
  answerActionId?: string;
  autoSyncOnChange?: boolean;
  status?: AiSearchAbilityStatus;
};

export type ContentResolutionAbilityState = {
  enabled: boolean;
  exposeContent?: boolean;
  exposeDeps?: boolean;
  depWhitelist?: string[];
  depAliases?: Record<string, string>;
  contentActionId?: string;
  registryActionId?: string;
};

export type AbilitiesResponse = {
  assetId: string;
  abilities: {
    'ai-search': AiSearchAbilityState;
    'content-resolution': ContentResolutionAbilityState;
  };
};

// --- INTERNAL HELPERS ---
const API_BASE_URL = '/api';

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.headers.get('content-type')?.includes('application/problem+json')) {
    const p = await response.json().catch(() => ({}));
    const msg = p?.title || p?.detail || `Request failed with status ${response.status}`;
    throw new Error(msg);
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
    const errorMessage = errorData.error || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }
  return response.json();
}

function jsonHeaders(): HeadersInit {
  return { 'Content-Type': 'application/json' };
}

// --- PUBLIC SERVICE ---
export const abilityService = {
  async getAbilities(assetId: string): Promise<AbilitiesResponse> {
    const res = await fetch(`${API_BASE_URL}/assets/${assetId}/abilities`, { credentials: 'include' });
    return handleResponse<AbilitiesResponse>(res);
  },

  async updateAiSearch(assetId: string, state: Partial<AiSearchAbilityState>): Promise<{ abilityId: string; state: AiSearchAbilityState }> {
    const res = await fetch(`${API_BASE_URL}/assets/${assetId}/abilities/ai-search`, {
      method: 'PUT',
      headers: jsonHeaders(),
      credentials: 'include',
      body: JSON.stringify(state),
    });
    return handleResponse(res);
  },

  async resyncAiSearch(assetId: string): Promise<{ abilityId: string; state: AiSearchAbilityState }> {
    const res = await fetch(`${API_BASE_URL}/assets/${assetId}/abilities/ai-search/resync`, {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  async updateContentResolution(assetId: string, state: ContentResolutionAbilityState): Promise<{ abilityId: string; state: ContentResolutionAbilityState }> {
    const res = await fetch(`${API_BASE_URL}/assets/${assetId}/abilities/content-resolution`, {
      method: 'PUT',
      headers: jsonHeaders(),
      credentials: 'include',
      body: JSON.stringify(state),
    });
    return handleResponse(res);
  },
};

export default abilityService;
